import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, Grid, TextField, Typography, useTheme } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { format } from 'date-fns';

import supportChatService from '../../services/supportChatService';
import CustomPagination from '../../components/common/CustomPagination';

const formatDateTime = (value) => {
  if (!value) {
    return '-';
  }
  try {
    return format(new Date(value), 'yyyy-MM-dd HH:mm');
  } catch (error) {
    return value;
  }
};

const getDisplayName = (user) => {
  if (!user) {
    return '-';
  }
  return user.fullname || user.username || user.email || user.phone || `User ${user.id}`;
};

export default function SupportAgentRatingsPage() {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [agentStats, setAgentStats] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [pagination, setPagination] = useState({ page: 0, pageSize: 25, rowCount: 0 });
  const [agentPage, setAgentPage] = useState(0);
  const [agentRowsPerPage, setAgentRowsPerPage] = useState(10);
  const [agentCustomRowsActive, setAgentCustomRowsActive] = useState(false);
  const [agentCustomRowsInput, setAgentCustomRowsInput] = useState('');
  const [ratingsCustomRowsActive, setRatingsCustomRowsActive] = useState(false);
  const [ratingsCustomRowsInput, setRatingsCustomRowsInput] = useState('');
  const [filters, setFilters] = useState({
    agent_id: '',
    start_date: '',
    end_date: '',
  });

  const fetchRatings = useCallback(async (next = {}) => {
    try {
      setLoading(true);
      setError('');
      const page = typeof next.page === 'number' ? next.page : pagination.page;
      const pageSize = typeof next.pageSize === 'number' ? next.pageSize : pagination.pageSize;

      const params = {
        page: page + 1,
        page_size: pageSize,
      };
      if (filters.agent_id) {
        params.agent_id = filters.agent_id;
      }
      if (filters.start_date) {
        params.start_date = filters.start_date;
      }
      if (filters.end_date) {
        params.end_date = filters.end_date;
      }

      const data = await supportChatService.listAgentRatings(params);
      setAgentStats(Array.isArray(data.agent_stats) ? data.agent_stats : []);
      setRatings(Array.isArray(data.results) ? data.results : []);
      setPagination((prev) => ({
        ...prev,
        page,
        pageSize,
        rowCount: Number(data.count || 0),
      }));
    } catch (err) {
      console.error('Failed to load support agent ratings', err);
      setError(err?.response?.data?.detail || 'Failed to load ratings.');
    } finally {
      setLoading(false);
    }
  }, [filters.agent_id, filters.end_date, filters.start_date, pagination.page, pagination.pageSize]);

  useEffect(() => {
    fetchRatings({ page: 0 });
  }, [fetchRatings]);

  useEffect(() => {
    const totalPages = Math.ceil(agentStats.length / agentRowsPerPage) || 1;
    if (agentPage > totalPages - 1) {
      setAgentPage(0);
    }
  }, [agentPage, agentRowsPerPage, agentStats.length]);

  const handleAgentPageChange = (newPage) => {
    setAgentPage(Math.max(0, newPage));
  };

  const handleAgentRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setAgentCustomRowsActive(true);
      setAgentCustomRowsInput(agentRowsPerPage.toString());
      return;
    }
    setAgentRowsPerPage(Number(value));
    setAgentPage(0);
    setAgentCustomRowsActive(false);
  };

  const handleAgentCustomRowsInputChange = (e) => {
    setAgentCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };

  const handleAgentCustomRowsInputBlur = () => {
    const val = parseInt(agentCustomRowsInput, 10);
    if (val && val > 0) {
      setAgentRowsPerPage(val);
      setAgentPage(0);
    }
    setAgentCustomRowsActive(false);
  };

  const handleRatingsPageChange = (newPage) => {
    const nextPage = Math.max(0, newPage);
    fetchRatings({ page: nextPage, pageSize: pagination.pageSize });
  };

  const handleRatingsRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setRatingsCustomRowsActive(true);
      setRatingsCustomRowsInput(pagination.pageSize.toString());
      return;
    }
    const nextPageSize = Number(value);
    fetchRatings({ page: 0, pageSize: nextPageSize });
    setRatingsCustomRowsActive(false);
  };

  const handleRatingsCustomRowsInputChange = (e) => {
    setRatingsCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };

  const handleRatingsCustomRowsInputBlur = () => {
    const val = parseInt(ratingsCustomRowsInput, 10);
    if (val && val > 0) {
      fetchRatings({ page: 0, pageSize: val });
    }
    setRatingsCustomRowsActive(false);
  };

  const agentColumns = useMemo(() => [
    {
      field: 'agent',
      headerName: 'Agent',
      flex: 1.2,
      minWidth: 220,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {getDisplayName(params?.row?.agent)}
        </Typography>
      ),
    },
    {
      field: 'average_rating',
      headerName: 'Avg Rating',
      flex: 0.6,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {Number(params?.row?.average_rating || 0).toFixed(2)}
        </Typography>
      ),
    },
    {
      field: 'rating_count',
      headerName: 'Ratings',
      flex: 0.5,
      minWidth: 110,
      renderCell: (params) => (
        <Typography variant="body2">
          {params?.row?.rating_count || 0}
        </Typography>
      ),
    },
  ], []);

  const ratingColumns = useMemo(() => [
    {
      field: 'created_at',
      headerName: 'Date',
      flex: 0.8,
      minWidth: 160,
      renderCell: ({ value }) => formatDateTime(value),
    },
    {
      field: 'agent',
      headerName: 'Agent',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => getDisplayName(params?.row?.agent),
    },
    {
      field: 'user',
      headerName: 'Customer',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => getDisplayName(params?.row?.user),
    },
    {
      field: 'rating',
      headerName: 'Rating',
      flex: 0.5,
      minWidth: 110,
    },
    {
      field: 'chat_session',
      headerName: 'Session',
      flex: 1.2,
      minWidth: 260,
      renderCell: ({ value }) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {value}
        </Typography>
      ),
    },
  ], []);

  const agentRows = useMemo(
    () => agentStats.map((row, idx) => ({ id: row.agent?.id || `agent_${idx}`, ...row })),
    [agentStats]
  );
  const agentTotalPages = Math.ceil(agentRows.length / agentRowsPerPage) || 1;
  const agentDisplayRows = useMemo(() => {
    const start = agentPage * agentRowsPerPage;
    return agentRows.slice(start, start + agentRowsPerPage);
  }, [agentPage, agentRows, agentRowsPerPage]);

  const ratingsTotalPages = Math.ceil(pagination.rowCount / pagination.pageSize) || 1;

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden',
    }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: (theme) => theme.palette.primary.main }}>
          Support Agent Ratings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Internal-only ratings linked to live support chat sessions.
        </Typography>
      </Box>

      {error ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : null}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Agent ID"
                value={filters.agent_id}
                onChange={(event) => setFilters((prev) => ({ ...prev, agent_id: event.target.value }))}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Start date"
                type="date"
                value={filters.start_date}
                onChange={(event) => setFilters((prev) => ({ ...prev, start_date: event.target.value }))}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="End date"
                type="date"
                value={filters.end_date}
                onChange={(event) => setFilters((prev) => ({ ...prev, end_date: event.target.value }))}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Button
                variant="contained"
                onClick={() => {
                  setAgentPage(0);
                  fetchRatings({ page: 0 });
                }}
                disabled={loading}
                sx={{
                  backgroundColor: (theme) => theme.palette.primary.main,
                  '&:hover': { backgroundColor: (theme) => theme.palette.primary.dark },
                }}
              >
                Apply Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>
            Agent Summary
          </Typography>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <DataGrid
              autoHeight
              rows={agentDisplayRows}
              columns={agentColumns}
              loading={loading}
              disableRowSelectionOnClick
              hideFooter
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: theme.palette.primary.contrastText,
                  fontWeight: 'bold',
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: theme.palette.primary.contrastText,
                },
              }}
            />
          </Box>
          <CustomPagination
            page={agentPage}
            totalPages={agentTotalPages}
            rowsPerPage={agentRowsPerPage}
            onPageChange={handleAgentPageChange}
            onRowsPerPageChange={handleAgentRowsPerPageChange}
            customRowsActive={agentCustomRowsActive}
            customRowsInput={agentCustomRowsInput}
            onCustomRowsInputChange={handleAgentCustomRowsInputChange}
            onCustomRowsInputBlur={handleAgentCustomRowsInputBlur}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>
            Recent Ratings
          </Typography>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <DataGrid
              autoHeight
              rows={ratings}
              columns={ratingColumns}
              loading={loading}
              disableRowSelectionOnClick
              hideFooter
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: theme.palette.primary.contrastText,
                  fontWeight: 'bold',
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: theme.palette.primary.contrastText,
                },
              }}
            />
          </Box>
          <CustomPagination
            page={pagination.page}
            totalPages={ratingsTotalPages}
            rowsPerPage={pagination.pageSize}
            onPageChange={handleRatingsPageChange}
            onRowsPerPageChange={handleRatingsRowsPerPageChange}
            customRowsActive={ratingsCustomRowsActive}
            customRowsInput={ratingsCustomRowsInput}
            onCustomRowsInputChange={handleRatingsCustomRowsInputChange}
            onCustomRowsInputBlur={handleRatingsCustomRowsInputBlur}
          />
        </CardContent>
      </Card>
    </Box>
  );
}
